import {
    ChangeDetectionStrategy,
    Component,
    ViewEncapsulation,
    computed,
    input,
    output,
} from "@angular/core";
import { provideTranslocoScope, TranslocoPipe } from "@jsverse/transloco";
import { CerrixAvatarComponent } from "@app/shared/atoms/cerrix-avatar/cerrix-avatar.component";

/**
 * Shows a group of people as overlapping avatars, with the ones beyond maxVisible
 * collapsed into a counter, and optionally an add button that asks for more.
 */
@Component({
    selector: "app-cerrix-avatar-group",
    imports: [CerrixAvatarComponent, TranslocoPipe],
    templateUrl: "./cerrix-avatar-group.component.html",
    styleUrl: "./cerrix-avatar-group.component.scss",
    providers: [
        provideTranslocoScope({
            scope: "shared/molecules/cerrix-avatar-group",
            alias: "cerrix-avatar-group",
        }),
    ],
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CerrixAvatarGroupComponent {
    /** Full names of the people in the group, in the order they should be shown. */
    readonly names = input.required<string[]>();

    /** Names past this count collapse into a "+N" circle. */
    readonly maxVisible = input(3);

    /** Shown instead of the avatars when there is nobody and no add button; falls back to a translated default. */
    readonly emptyText = input<string>();

    readonly showAddButton = input(false);

    /** Names the add button for screen readers and on hover; falls back to a translated default. */
    readonly addLabel = input<string>();

    /** When false, shows fade-out effect on the last avatar. */
    readonly readonly = input(true);

    readonly addRequested = output<void>();

    protected readonly visibleNames = computed(() => this.names().slice(0, this.maxVisible()));
    protected readonly hiddenNames = computed(() => this.names().slice(this.maxVisible()));

    /** The collapsed names stay readable on hover; the counter alone says nothing. */
    protected readonly hiddenTitle = computed(() => this.hiddenNames().join(", "));

    /** Show fade-out effect on last avatar only in editable mode with 3+ visible avatars. */
    protected readonly showFadeOutEffect = computed(
        () => !this.readonly() && this.visibleNames().length >= 3,
    );
}
